import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Trash2,
  Save,
  Loader2,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Palette,
  AlertCircle,
  Check,
  Receipt,
  CreditCard
} from 'lucide-react';
import { useCompany } from '../../../../contexts/CompanyContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { settingsService } from '../../../../services/settingsService';

// Australian timezones
const TIMEZONES = [
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST)' },
  { value: 'Australia/Hobart', label: 'Hobart (AEST/AEDT)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC', label: 'UTC' }
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday'
};

const CompanyProfileTab = () => {
  const { currentCompany, refreshCompanies } = useCompany();
  const { hasPermission, userRole } = usePermissions();
  // Primary owner always has full access
  const canEdit = userRole === 'primary_owner' || userRole === 'owner' || hasPermission('manage_company_settings');
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    phone: '',
    contact_email: '',
    website: '',
    timezone: 'Australia/Sydney',
    business_hours: {}
  });

  const [logoPreview, setLogoPreview] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    primary_color: '#FF7A00',
    secondary_color: '#00C47D',
    abn: '',
    show_gst_on_quotes: true,
    bank_name: '',
    bsb: '',
    account_number: '',
    show_bank_details_on_quotes: true
  });

  useEffect(() => {
    if (currentCompany?.id) {
      loadProfile();
    }
  }, [currentCompany?.id]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to load company profile - use basic data if extended fields don't exist
      let profile = currentCompany || {};
      try {
        const { data } = await settingsService.getCompanyProfile(currentCompany.id);
        profile = data || profile;
      } catch (e) {
        // Extended columns might not exist yet, use company context data
        console.warn('Using basic company data:', e.message);
      }

      setFormData({
        company_name: profile.company_name || currentCompany?.company_name || '',
        address_line1: profile.address_line1 || '',
        address_line2: profile.address_line2 || '',
        city: profile.city || '',
        state: profile.state || '',
        postcode: profile.postcode || '',
        country: profile.country || 'Australia',
        phone: profile.phone || '',
        contact_email: profile.contact_email || '',
        website: profile.website || '',
        timezone: profile.timezone || 'Australia/Sydney',
        business_hours: profile.business_hours || getDefaultBusinessHours()
      });
      setLogoPreview(profile.logo_url);

      // Load company settings for branding and GST/Bank details
      try {
        const { data: settings } = await settingsService.getCompanySettings(currentCompany.id);
        setCompanySettings({
          primary_color: settings?.primary_color || '#FF7A00',
          secondary_color: settings?.secondary_color || '#00C47D',
          abn: settings?.abn || '',
          show_gst_on_quotes: settings?.show_gst_on_quotes !== false,
          bank_name: settings?.bank_name || '',
          bsb: settings?.bsb || '',
          account_number: settings?.account_number || '',
          show_bank_details_on_quotes: settings?.show_bank_details_on_quotes !== false
        });
      } catch (e) {
        // Settings table might not exist yet
        console.warn('Company settings not available:', e.message);
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      // Don't show error - just use default values
    } finally {
      setLoading(false);
    }
  };

  const getDefaultBusinessHours = () => ({
    monday: { start: '09:00', end: '17:00', closed: false },
    tuesday: { start: '09:00', end: '17:00', closed: false },
    wednesday: { start: '09:00', end: '17:00', closed: false },
    thursday: { start: '09:00', end: '17:00', closed: false },
    friday: { start: '09:00', end: '17:00', closed: false },
    saturday: { start: null, end: null, closed: true },
    sunday: { start: null, end: null, closed: true }
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (file.size > 512000) {
      setError('Logo must be less than 500KB');
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Logo must be PNG, JPG, or WebP');
      return;
    }

    try {
      setUploadingLogo(true);
      setError(null);
      const { url } = await settingsService.uploadLogo(currentCompany.id, file);
      setLogoPreview(url);
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      // Show the actual error message for debugging
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      setUploadingLogo(true);
      await settingsService.deleteLogo(currentCompany.id);
      setLogoPreview(null);
      setSuccess('Logo removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting logo:', err);
      setError('Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleBusinessHoursChange = (day, field, value) => {
    setFormData(prev => ({
      ...prev,
      business_hours: {
        ...prev.business_hours,
        [day]: {
          ...prev.business_hours[day],
          [field]: value
        }
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      // Update company profile
      await settingsService.updateCompanyProfile(currentCompany.id, {
        address_line1: formData.address_line1,
        address_line2: formData.address_line2,
        city: formData.city,
        state: formData.state,
        postcode: formData.postcode,
        country: formData.country,
        phone: formData.phone,
        contact_email: formData.contact_email,
        website: formData.website,
        timezone: formData.timezone,
        business_hours: formData.business_hours
      });

      // Update branding colors and GST/Bank details in company settings
      await settingsService.updateCompanySettings(currentCompany.id, {
        primary_color: companySettings.primary_color,
        secondary_color: companySettings.secondary_color,
        abn: companySettings.abn || null,
        show_gst_on_quotes: companySettings.show_gst_on_quotes,
        bank_name: companySettings.bank_name || null,
        bsb: companySettings.bsb || null,
        account_number: companySettings.account_number || null,
        show_bank_details_on_quotes: companySettings.show_bank_details_on_quotes
      });

      await refreshCompanies?.();
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Company Profile</h2>
          <p className="text-text-secondary text-sm mt-1">
            Update your company's public information and branding
          </p>
        </div>
        {!canEdit && (
          <span className="px-3 py-1 bg-border/50 text-text-secondary rounded-full text-xs">
            View Only
          </span>
        )}
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* Logo Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Building2 size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Company Logo</h3>
        </div>
        <div className="flex items-center space-x-6">
          <div className="w-24 h-24 bg-surface rounded-xl flex items-center justify-center overflow-hidden border border-border">
            {uploadingLogo ? (
              <Loader2 size={24} className="text-orange-500 animate-spin" />
            ) : logoPreview ? (
              <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
            ) : (
              <Building2 size={32} className="text-text-tertiary" />
            )}
          </div>
          {canEdit && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <label className="cursor-pointer inline-flex items-center px-4 py-2 bg-surface text-text-primary rounded-lg hover:bg-surface-light transition-colors text-sm">
                  <Upload size={16} className="mr-2" />
                  Upload Logo
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={uploadingLogo}
                  />
                </label>
                {logoPreview && (
                  <button
                    onClick={handleDeleteLogo}
                    disabled={uploadingLogo}
                    className="p-2 text-text-secondary hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
              <p className="text-xs text-text-secondary">PNG, JPG, WebP up to 500KB. 200x200px recommended.</p>
            </div>
          )}
        </div>
      </div>

      {/* Company Name (Read-only) */}
      <div className="border-b border-border pb-6">
        <label className="block text-text-secondary text-sm mb-2">Company Name</label>
        <input
          type="text"
          value={formData.company_name}
          disabled
          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed"
        />
        <p className="text-xs text-text-secondary mt-1">Company name cannot be changed here.</p>
      </div>

      {/* Address Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <MapPin size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Address</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">Street Address</label>
            <input
              type="text"
              value={formData.address_line1}
              onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="123 Main Street"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">Address Line 2</label>
            <input
              type="text"
              value={formData.address_line2}
              onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Suite 100 (optional)"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">City</label>
            <input
              type="text"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Sydney"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">State</label>
            <input
              type="text"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="NSW"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Postcode</label>
            <input
              type="text"
              value={formData.postcode}
              onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="2000"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Country</label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Australia"
            />
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Phone size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Contact Information</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Phone size={14} className="inline mr-1" /> Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="+61 2 1234 5678"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">
              <Mail size={14} className="inline mr-1" /> Email
            </label>
            <input
              type="email"
              value={formData.contact_email}
              onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="contact@company.com"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-text-secondary text-sm mb-2">
              <Globe size={14} className="inline mr-1" /> Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) => setFormData({ ...formData, website: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="https://www.company.com"
            />
          </div>
        </div>
      </div>

      {/* GST / ABN Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Receipt size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">GST / ABN Details</h3>
        </div>
        <p className="text-text-secondary text-sm mb-4">
          These details will appear on your quotations and invoices.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">ABN (Australian Business Number)</label>
            <input
              type="text"
              value={companySettings.abn}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9\s]/g, '');
                setCompanySettings({ ...companySettings, abn: value });
              }}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="12 345 678 901"
            />
          </div>
          <div className="flex items-center">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={companySettings.show_gst_on_quotes}
                onChange={(e) => setCompanySettings({ ...companySettings, show_gst_on_quotes: e.target.checked })}
                disabled={!canEdit}
                className="w-4 h-4 rounded border-border text-orange-500 focus:ring-orange-500 bg-surface"
              />
              <span className="text-text-primary text-sm">Show GST/ABN details on quotations</span>
            </label>
          </div>
        </div>
      </div>

      {/* Bank Details Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <CreditCard size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Bank Details</h3>
        </div>
        <p className="text-text-secondary text-sm mb-4">
          Bank details for payment information on quotations.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Bank Name</label>
            <input
              type="text"
              value={companySettings.bank_name}
              onChange={(e) => setCompanySettings({ ...companySettings, bank_name: e.target.value })}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="Commonwealth Bank"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">BSB</label>
            <input
              type="text"
              value={companySettings.bsb}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9-]/g, '').slice(0, 7);
                setCompanySettings({ ...companySettings, bsb: value });
              }}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="062-000"
            />
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Account Number</label>
            <input
              type="text"
              value={companySettings.account_number}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, '');
                setCompanySettings({ ...companySettings, account_number: value });
              }}
              disabled={!canEdit}
              className="w-full px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder="12345678"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={companySettings.show_bank_details_on_quotes}
              onChange={(e) => setCompanySettings({ ...companySettings, show_bank_details_on_quotes: e.target.checked })}
              disabled={!canEdit}
              className="w-4 h-4 rounded border-border text-orange-500 focus:ring-orange-500 bg-surface"
            />
            <span className="text-text-primary text-sm">Show bank details on quotations</span>
          </label>
        </div>
      </div>

      {/* Business Hours Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Business Hours</h3>
        </div>

        <div className="mb-4">
          <label className="block text-text-secondary text-sm mb-2">Timezone</label>
          <select
            value={formData.timezone}
            onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            disabled={!canEdit}
            className="w-full md:w-64 px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          {DAYS.map(day => {
            const hours = formData.business_hours[day] || { start: '09:00', end: '17:00', closed: false };
            return (
              <div key={day} className="flex items-center space-x-4">
                <span className="w-24 text-text-secondary text-sm">{DAY_LABELS[day]}</span>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={hours.closed}
                    onChange={(e) => handleBusinessHoursChange(day, 'closed', e.target.checked)}
                    disabled={!canEdit}
                    className="rounded border-border text-orange-500 focus:ring-orange-500 bg-surface"
                  />
                  <span className="text-text-secondary text-sm">Closed</span>
                </label>
                {!hours.closed && (
                  <>
                    <input
                      type="time"
                      value={hours.start || '09:00'}
                      onChange={(e) => handleBusinessHoursChange(day, 'start', e.target.value)}
                      disabled={!canEdit}
                      className="px-3 py-1 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <span className="text-text-secondary">to</span>
                    <input
                      type="time"
                      value={hours.end || '17:00'}
                      onChange={(e) => handleBusinessHoursChange(day, 'end', e.target.value)}
                      disabled={!canEdit}
                      className="px-3 py-1 bg-surface border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Branding Colors Section */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Palette size={16} className="text-text-secondary" />
            <h3 className="text-sm font-medium text-text-secondary">Branding Colors</h3>
          </div>
          <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded text-xs">
            Coming Soon
          </span>
        </div>
        <p className="text-text-secondary text-sm mb-4">
          Custom branding colors will be applied to your company's invoices and reports.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Primary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={companySettings.primary_color}
                onChange={(e) => setCompanySettings({ ...companySettings, primary_color: e.target.value })}
                disabled
                className="w-10 h-10 rounded-lg border border-border cursor-not-allowed opacity-50"
              />
              <input
                type="text"
                value={companySettings.primary_color}
                disabled
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed uppercase"
                placeholder="#FF7A00"
              />
            </div>
          </div>
          <div>
            <label className="block text-text-secondary text-sm mb-2">Secondary Color</label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={companySettings.secondary_color}
                disabled
                className="w-10 h-10 rounded-lg border border-border cursor-not-allowed opacity-50"
              />
              <input
                type="text"
                value={companySettings.secondary_color}
                disabled
                className="flex-1 px-4 py-2 bg-background border border-border rounded-lg text-text-secondary cursor-not-allowed uppercase"
                placeholder="#00C47D"
              />
            </div>
          </div>
        </div>
        <p className="text-xs text-text-secondary mt-2">
          Custom branding for invoices and reports is coming soon. Your company colors will be applied automatically once available.
        </p>
      </div>

      {/* Save Button */}
      {canEdit && (
        <div className="flex justify-end pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-text-primary rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} className="mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default CompanyProfileTab;
